import { Dropdown, InputGroupText, NumericTextField, TextField } from "app/components/CustomFormElements/syncFusionFormFields";
import { videoValidationSchema } from "app/helper/validationSchema/companyValidationSchema";
import { useFormik } from "formik";
import * as Yup from "yup";
import { actionError, actionSuccess, showLoader, hideLoader } from "middleware/actions/utilityAction";
import { fetchVideoById, editVideo, saveVideo, fetchVideoThumbUrl, fetchSingleCompany } from "middleware/services/companyApi";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";
import { useHistory } from "react-router-dom";
import { COMPANY_LOGO_IMG_SIZE } from "config";
import { setBreadCrumb } from "middleware/actions/breadCrumbAction";

const CompanyBasicVideo = (props) => {
  const { companyId, videoId } = props.computedMatch.params || {};
  const [companyName, setCompanyName] = useState("");
  const videoThumbRef = useRef(null);
  const videoThumbInputRef = useRef(null);
  const [imageError, setImageError] = useState("");
  const [oldData, setOldData] = useState(null);

  const dispatch = useDispatch();
  const history = useHistory();

  const initialFormValues = {
    videoTitle: "",
    videoDescription: "",
    videoSource: "Vimeo",
    videoId: "",
    videoDurationInMinute: "",
    videoThumbnail: "",
  };

  const breadCrumbLinks = [
    { linkUrl: "/company", linkName: `Company`, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: companyName, linkActive: false },
    { linkUrl: `/company/${companyId}/edit#basic`, linkName: "Basic", linkActive: false },
    { linkUrl: ``, linkName: "Video", linkActive: true },
  ];

  const formik = useFormik({
    initialValues: oldData || initialFormValues,
    validationSchema: Yup.object().shape(videoValidationSchema),
    enableReinitialize: true,
  });

  const readVideoThumnail = (imageFile) => {
    let reader = new FileReader();
    reader.onload = (e) => {
      videoThumbRef.current.setAttribute("src", e.target.result);
    };
    reader.readAsDataURL(imageFile);
  };

  const getCompanyName = async () => {
    if (companyId) {
      const fields = { fields: ["id", "companyName"] };
      const response = await fetchSingleCompany(companyId, { ...fields });
      setCompanyName(response.data.data.companyName);
    }
  };

  const handleReset = () => {
    formik.resetForm();
    if (props.editData) {
      formik.setValues(oldData);
    }
  };

  const onSaveData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      const response = await saveVideo(companyId, formData);
      if (response) {
        dispatch(hideLoader());
        dispatch(actionSuccess("Video saved successfully"));
        handleReset();
        history.goBack();
      }
    } catch (error) {
      dispatch(hideLoader());
      dispatch(actionError(error.data.message || "Something went wrong"));
    }
  };

  const onUpdateData = async (data) => {
    const formData = new FormData();
    for (let key in data) {
      formData.append(key, data[key]);
    }
    try {
      dispatch(showLoader());
      const response = await editVideo(companyId, videoId, formData);
      if (response) {
        dispatch(hideLoader());
        dispatch(actionSuccess("Video updated successfully"));
        handleReset();
        history.goBack();
      }
    } catch (error) {
      dispatch(hideLoader());
      dispatch(actionError(error.data.message || "Something went wrong"));
    }
  };

  const setFormikValues = (data) => {
    const oldData = {
      videoTitle: data.videoTitle ? data.videoTitle : "",
      videoDescription: data.videoDescription || "",
      videoSource: data.videoSource || "",
      videoId: data.videoId || "",
      videoDurationInMinute: data.videoDuration || "",
      videoThumbnail: data.videoThumbnail || "",
    };
    setOldData(oldData);
    setTimeout(() => {
      formik.setValues(oldData);
    }, 1);
  };

  const saveCompanyVideoData = () => {
    const { values } = formik;
    const companyVideoData = {
      videoTitle: values.videoTitle,
      videoDescription: values.videoDescription,
      videoSource: values.videoSource,
      videoId: values.videoId,
      videoDuration: values.videoDurationInMinute,
      videoThumbnail: values.videoThumbnail,
    };
    props.editData ? onUpdateData(companyVideoData) : onSaveData(companyVideoData);
  };

  const handleSubmitVideo = () => {
    formik.validateForm().then((res) => {
      if (Object.keys(res).length) {
        const touched = {};
        Object.keys(res).forEach((field) => {
          touched[field] = true;
        });
        formik.setFormikState({ ...formik, touched: touched, errors: res });
      } else {
        saveCompanyVideoData();
      }
    });
  };

  useEffect(() => {
    if (props.editData && companyId && videoId) {
      formik.resetForm();
      fetchVideoById(companyId, videoId)
        .then((res) => {
          setFormikValues(res.data?.data?.companyVideo || []);
        })
        .catch((err) => {
          dispatch(actionError(err?.data?.message || "Something went wrong"));
        });
    } else {
      videoThumbInputRef.current.value = "";
      formik.resetForm();
    }
  }, [props.editData, companyId, videoId]);

  useEffect(() => {
    dispatch(setBreadCrumb(breadCrumbLinks));
  }, [breadCrumbLinks]);

  useEffect(() => {
    getCompanyName();
  }, [companyId]);

  return (
    <div className="p-0 m-3 company-basic-modal">
      <div className="modal-header">Company - Video</div>
      <div className="modal-body">
        <div className="video-content">
          <div className="form-row">
            <div className="video-title">
              {" "}
              <TextField formik={formik} placeholder="Title *" id="videoTitle" name="videoTitle" required="required" maxLength="250" />
            </div>
            <div className="video-source">
              {" "}
              <Dropdown
                formik={formik}
                required="required"
                placeholder="Video Source"
                id="videoSource"
                name="videoSource"
                //  fields={{ text: "label", value: "value" }}
                dataSource={["Vimeo", "YouTube"]}
              />
            </div>
            <div className="video-input-group">
              <InputGroupText
                formik={formik}
                inputGroupText={
                  formik.values.videoSource === "Vimeo"
                    ? "https://player.vimeo.com/video/"
                    : (formik.values.videoSource === "YouTube" && "https://www.youtube.com/") || ""
                }
                type="text"
                id="videoId"
                placeholder={`Video ID *`}
                name="videoId"
              />
              <div className="icon-tip">
                <i
                  data-tip={`e.g link : https://${
                    formik.values.videoSource === "Vimeo" ? "vimeo.com" : "youtube.com"
                  }/428464282/899849fa06,<br/> id : 428464282/899849fa06`}
                  className="fas fa-info"
                ></i>
                <ReactTooltip multiline={true} delay={1000} />
              </div>
            </div>
            <div className="video-duration">
              <NumericTextField
                formik={formik}
                format="n0"
                min={0}
                id="videoDurationInMinute"
                placeholder="Duration in Minutes *"
                name="videoDurationInMinute"
              />
            </div>
          </div>
          <div className="p-3">
            <p style={{ fontSize: "14px", fontWeight: "bold", color: "#4B4B55" }}>Upload Thumbnail *</p>
            <div className="row m-0">
              <div className="product-logo-holder p-0">
                <div>
                  <img
                    src={formik.values?.videoThumbnail ? fetchVideoThumbUrl(formik.values.videoThumbnail) : "/images/default_company_image.jpg"}
                    alt="Product-image"
                    ref={videoThumbRef}
                    className="object-cover"
                  />
                </div>
                <div className="product-logo-action" style={{ top: "2px", right: "6px" }}>
                <span onClick={() => videoThumbInputRef.current.click()}>
                    <i className="fa fa-pencil-alt" aria-hidden="true"></i>
                  </span>
                  {formik.values?.videoThumbnail && (
                <span onClick={() => {
                formik.setFieldValue("videoThumbnail", null);
                if (videoThumbInputRef.current) {
                videoThumbInputRef.current.value = "";
                 }
                setImageError("");
                videoThumbRef.current.src = "/images/default_company_image.jpg"; // Reset preview
                }}>
              <i className="fa fa-trash ml-1" aria-hidden="true"></i>
                    </span>
                  )}
                </div>
                <input
                  type="file"
                  className="d-none"
                  accept=".jpg, .png"
                  id="videoThumbnail"
                  name="videoThumbnail"
                  ref={videoThumbInputRef}
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (e.target.files.length > 0) {
                      if ((file.type == "image/png" || file.type == "image/jpg" || file.type == "image/jpeg") && file.size < COMPANY_LOGO_IMG_SIZE) {
                        setImageError("");
                        readVideoThumnail(file);
                        if (videoThumbInputRef.current.files.length)
                          formik.setValues({ ...formik.values, videoThumbnail: videoThumbInputRef.current.files[0] });
                      } else {
                        setImageError("Only JPG/JPEG or PNG files are allowed. Max file size allowed is 10MB.");
                      }
                    }
                  }}
                />
              </div>
              <div className="ml-3">
                <p className="mb-0" style={{ fontSize: "14px", fontWeight: "bold", color: "#4B4B55" }}>
                  Note:
                </p>
                <ul className="ml-4" style={{ fontSize: "14px" }}>
                  <li>Only JPG/JPEG or PNG files are allowed.</li>
                  <li>Max file size allowed is 10MB.</li>
                  <li>Thumbnail Dimensions: 350W x 200H</li>
                </ul>
              </div>
            </div>
            <div className={`image-error ${formik.touched.videoThumbnail === true ? "show" : "hide"}`} role="alert">
              <small>{formik.errors.videoThumbnail ? formik.errors.videoThumbnail : ""}</small>
            </div>
            <div className={`image-error ${imageError ? "show" : "hide"}`} role="alert">
              <small>{imageError ? imageError : ""}</small>
            </div>
          </div>
        </div>

        <div className="text-right border-top p-3 mt-auto">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleReset();
              setImageError("");
              history.goBack();
            }}
            className="btn btn-outline-dark mr-3"
          >
            Cancel
          </button>

          {true ? (
            <button type="button" className="btn btn-primary" onClick={handleSubmitVideo} disabled={false}>
              {props?.editData ? "Update" : "Save"}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CompanyBasicVideo;
